<script setup lang="ts">
  defineOptions({
    name: 'ChatMessageBubble',
  })

  import { computed, defineAsyncComponent, ref, watch, type PropType } from 'vue'

  import { useI18n } from 'vue-i18n'
  import { useAuth } from '@common/auth-layer/composables/useAuth'

  import FileAttachmentCard from '@common/shared-ui-layer/components/FileAttachmentCard.vue'

  import CPAvatar from '@ui/components/CPAvatar.vue'

  import { resolveVoicePlayerVariant } from '@common/media-layer/config/uploaded-audio-player.config'
  import { isInlineAudioShape, isUploadedAudioShape as isUploadedAudioMedia } from '@common/media-layer/utils/voice-note-shape'
  import { buildCommunitiesVideoAnalyticsContext } from '@communities/common-layer/utils/assetsDrmAnalyticsContext.helper'
  import { MessageSignal, MessageStatus } from '../../types/chats.enums'

  import type { IConversationRecipient, IMessage } from '../../types/conversation.types'

  const OptimisedVideo = defineAsyncComponent(() => import('@common/media-layer/components/OptimisedVideo.vue'))

  const OptimisedImage = defineAsyncComponent(() => import('@common/media-layer/components/OptimisedImage.vue'))

  const VoiceNoteBubble = defineAsyncComponent(() => import('@communities/comments-layer/components/atoms/VoiceNoteBubble.vue'))

  const props = defineProps({
    message: {
      type: Object as PropType<IMessage>,
      required: true,
    },
    isSent: {
      type: Boolean,
      default: false,
    },
    showAvatar: {
      type: Boolean,
      default: false,
    },
    showTime: {
      type: Boolean,
      default: false,
    },
    recipient: {
      type: Object as PropType<IConversationRecipient | null>,
      default: null,
    },
    /** Immediate next-adjacent autoplay resolver (omit to disable). */
    resolveNextAutoplayKey: {
      type: Function as PropType<() => string | null>,
      default: undefined,
    },
  })

  const { locale, t } = useI18n()

  const { store: authStore } = useAuth()

  const videoAnalyticsContext = computed(() =>
    buildCommunitiesVideoAnalyticsContext({
      locationId: props.message.locationId || props.message.originLocationId,
      contactId: authStore.contactId,
      groupId: props.message.conversationId,
      itemId: props.message._id || props.message.uniqueRefId,
    })
  )

  const voiceNoteLabels = computed(() => ({
    play: t('chats.voiceNote.play'),
    pausePlayback: t('chats.voiceNote.pausePlayback'),
    waveform: t('chats.voiceNote.waveform'),
    timeline: t('chats.voiceNote.timeline'),
    changeSpeed: t('chats.voiceNote.changeSpeed', { speed: '{speed}' }),
  }))

  const media = computed(() => props.message.content?.media)

  const mediaUrl = computed(() => media.value?.url || '')

  const mediaLicenseId = computed(() => media.value?.licenseId)

  const mediaType = computed(() => media.value?.type?.toLowerCase() || '')

  const isGif = computed(() => mediaType.value.includes('gif'))

  const isVideo = computed(() => mediaType.value.includes('video'))

  const isImage = computed(() => mediaType.value.includes('image') && !isGif.value)

  // Recorded voice note or uploaded audio — both use an inline player bubble.
  const isUploadedAudio = computed(() => isUploadedAudioMedia(media.value))
  const isInlineAudio = computed(() => isInlineAudioShape(media.value))

  const isFile = computed(() => !!media.value && !isImage.value && !isVideo.value && !isGif.value && !isInlineAudio.value)

  const isMediaBubble = computed(
    () => !!media.value && !props.message.content?.text && props.message.content?.signal !== MessageSignal.TYPING
  )

  const isSending = computed(() => props.message.status === MessageStatus.SENDING)

  const isFailed = computed(() => props.message.status === MessageStatus.FAILED)

  // Stable id for the single-active playback coordinator. Optimistic and delivered
  // rows must resolve to the same key so a playing bubble keeps identity across ACK.
  const voicePlaybackKey = computed(
    () => (props.message as { tempId?: string }).tempId || (props.message as { uniqueRefId?: string }).uniqueRefId || props.message._id
  )

  const getMediaAspectRatio = () => {
    const width = Number(media.value?.width)
    const height = Number(media.value?.height)

    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
      return null
    }

    return width / height
  }

  const getMediaBoxStyle = (ratio: number) => {
    const safeRatio = Math.max(ratio, 0.01)
    const isPortrait = safeRatio < 1

    if (isPortrait) {
      const maxHeight = 360
      const portraitWidth = Math.max(1, Math.round(maxHeight * safeRatio))

      return {
        width: `${portraitWidth}px`,
        maxWidth: '100%',
        aspectRatio: `${safeRatio}`,
        height: 'auto',
      }
    }

    return {
      width: '100%',
      maxWidth: '100%',
      aspectRatio: `${safeRatio}`,
      height: 'auto',
    }
  }

  const mediaBoxStyle = computed(() => {
    if (!media.value || isFile.value) {
      return {}
    }

    const ratio = getMediaAspectRatio()
    if (!ratio) {
      return isVideo.value ? getMediaBoxStyle(16 / 9) : {}
    }

    return getMediaBoxStyle(ratio)
  })

  const mediaAlt = computed(() => {
    if (!media.value) return ''
    const fallback = isImage.value
      ? t('chats.messageEditor.attachments.images')
      : isVideo.value
        ? t('chats.messageEditor.attachments.videos')
        : isGif.value
          ? t('chats.messageEditor.attachments.gif')
          : t('chats.messageEditor.attachments.documents')
    return media.value.fileName || (media.value as { name?: string }).name || fallback
  })

  const fileAttachments = computed(() => {
    if (!media.value || !isFile.value || !media.value.url) return []
    return [
      {
        id: props.message._id,
        name: mediaAlt.value,
        url: media.value.url,
        size: media.value.fileSize,
        type: media.value.mimeType,
        licenseId: mediaLicenseId.value,
      },
    ]
  })

  const isMediaLoaded = ref(false)

  const handleMediaLoaded = () => {
    isMediaLoaded.value = true
  }

  const handleMediaError = () => {
    isMediaLoaded.value = true
  }

  const showMediaShimmer = computed(() => {
    return isMediaBubble.value && (isImage.value || isGif.value) && !isMediaLoaded.value
  })

  const resetMediaLoaded = () => {
    if (!media.value || isFile.value || isVideo.value) {
      isMediaLoaded.value = true
      return
    }
    isMediaLoaded.value = false
  }

  const bubbleClasses = computed(() => {
    // Voice notes render as a chat bubble with sent/received colors (like text),
    // not a neutral media tile, so mine vs the other person stays distinguishable.
    if (isMediaBubble.value && !isInlineAudio.value) {
      return 'chat-bubble-media'
    }
    return props.isSent ? 'chat-bubble-sent' : 'chat-bubble-received'
  })

  const bubblePaddingClass = computed(() => {
    if (isInlineAudio.value) return 'voice-bubble-pad'
    if (isMediaBubble.value) return 'p-1'
    return 'px-3 py-2'
  })

  const messageId = computed(() => props.message?._id || 'unknown')

  const formattedTime = computed(() => {
    if (!props.message.createdAt) return ''
    const date = new Date(props.message.createdAt)
    return new Intl.DateTimeFormat(locale.value, {
      hour: 'numeric',
      minute: '2-digit',
    }).format(date)
  })

  watch(
    [mediaUrl, mediaType],
    () => {
      resetMediaLoaded()
    },
    { immediate: true }
  )
</script>

<template>
  <div
    :id="`chat-message-row-${messageId}`"
    class="message-row flex w-full"
    :style="{ justifyContent: isSent ? 'flex-end' : 'flex-start' }"
  >
    <!-- Avatar (only for received messages) -->
    <div v-if="!isSent" class="mr-2 flex-shrink-0 self-end">
      <CPAvatar
        v-if="showAvatar"
        :src="recipient?.avatar"
        :title="recipient?.fullName || t('chats.user.defaultName')"
        class="h-8 w-8 bg-border"
      />
      <div v-else :id="`chat-message-avatar-placeholder-${messageId}`" class="h-8 w-8" />
    </div>

    <!-- Message Content -->
    <div class="message-column flex min-w-0 flex-col" :class="isSent ? 'items-end' : 'items-start'">
      <!-- Message Bubble -->
      <div :id="`chat-message-bubble-${messageId}`" :class="[bubbleClasses, bubblePaddingClass, 'min-w-0 max-w-full']">
        <!-- Text Message -->
        <div
          v-if="message.content?.text"
          :id="`chat-message-text-${messageId}`"
          v-sanitized-html="message.content.text"
          class="message-text whitespace-pre-wrap"
        />

        <!-- Typing Indicator -->
        <div
          v-else-if="message.content?.signal === MessageSignal.TYPING"
          :id="`chat-message-typing-${messageId}`"
          class="bouncing-dots flex items-center justify-center"
        >
          <span :id="`chat-message-typing-dot-1-${messageId}`" class="dot" />
          <span :id="`chat-message-typing-dot-2-${messageId}`" class="dot" />
          <span :id="`chat-message-typing-dot-3-${messageId}`" class="dot" />
        </div>

        <div
          v-else-if="media"
          class="message-media"
          :class="{ 'message-media--voice message-media--voice-fixed': isInlineAudio }"
          :style="isInlineAudio ? undefined : mediaBoxStyle"
        >
          <div v-if="showMediaShimmer" class="media-shimmer" />
          <OptimisedImage
            v-if="isImage || isGif"
            :license-id="mediaLicenseId"
            :src="mediaUrl"
            :alt="mediaAlt"
            :allow-full-screen-preview="true"
            rounded="rounded"
            :contain="true"
            class="message-media-image"
            @load="handleMediaLoaded"
            @error="handleMediaError"
          />
          <div v-else-if="isVideo" class="message-media-video">
            <OptimisedVideo
              :src="mediaUrl"
              :license-id="mediaLicenseId"
              :analytics-context="videoAnalyticsContext"
              :thumbnail-only="true"
              :open-in-gallery="true"
              class="message-media-video-player"
            />
          </div>
          <VoiceNoteBubble
            v-else-if="isInlineAudio"
            layout="chat"
            :playback-key="voicePlaybackKey"
            :audio-url="mediaUrl"
            :duration-sec="media?.durationSec || 0"
            :waveform-peaks="media?.waveformPeaks || []"
            :variant="resolveVoicePlayerVariant(isUploadedAudio)"
            :file-name="media?.fileName || ''"
            :resolve-next-autoplay-key="resolveNextAutoplayKey"
            :labels="voiceNoteLabels"
            class="message-media-voice message-media-voice--fixed"
          />
          <FileAttachmentCard v-else-if="isFile" :attachments="fileAttachments" :compact="true" class="message-media-file" />
        </div>

        <!-- Timestamp or Status (inside bubble) -->
        <div v-if="showTime && !isMediaBubble" class="mt-0.5 flex items-center justify-end">
          <span
            v-if="isSending || isFailed"
            :id="`chat-message-status-${messageId}`"
            class="timestamp"
            :class="{ 'message-status-failed': isFailed }"
          >
            {{ isSending ? t('chats.messageStatus.sending') : t('chats.messageStatus.failed') }}
          </span>
          <span v-else :id="`chat-message-timestamp-${messageId}`" class="timestamp">{{ formattedTime }}</span>
        </div>
      </div>

      <div
        v-if="showTime && isMediaBubble"
        :id="`chat-message-meta-${messageId}`"
        class="message-meta mt-0.5 flex items-center"
        :class="isSent ? 'justify-end' : 'justify-start'"
      >
        <span
          v-if="isSending || isFailed"
          :id="`chat-message-status-${messageId}`"
          class="timestamp"
          :class="{ 'message-status-failed': isFailed }"
        >
          {{ isSending ? t('chats.messageStatus.sending') : t('chats.messageStatus.failed') }}
        </span>
        <span v-else :id="`chat-message-timestamp-${messageId}`" class="timestamp">{{ formattedTime }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
  /* Sent message bubble */
  /* Cap is on the column, not a purged arbitrary utility. overflow-wrap:anywhere
     lets a long URL count as wrappable so the column stays inside this max. */
  .message-column {
    max-width: 80%;
  }

  .chat-bubble-sent {
    border-radius: 8px 8px 0 8px;
    background: var(--cp-background-500);
    border: 1px solid var(--cp-border-500);
    max-width: 100%;
    min-width: 0;
  }

  /* Received message bubble - blue background */
  .chat-bubble-received {
    border-radius: 8px 8px 8px 0;
    background: var(--cp-primary-50);
    border: 1px solid var(--cp-border-500);
    max-width: 100%;
    min-width: 0;
  }

  .chat-bubble-media {
    border: 1px solid var(--cp-border-500);
    border-radius: 8px;
    background: var(--cp-background-500);
    width: fit-content;
    max-width: 100%;
  }

  .voice-bubble-pad {
    padding: 0.375rem 0.5rem;
  }

  /* Message text - 14px, gray-900 */
  .message-text {
    font-family: inherit;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.43;
    color: var(--cp-font-900);
    margin: 0;
    max-width: 100%;
    min-width: 0;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  /* Reset margins on nested p tags from rich text content.
     anywhere (not break-word) so a URL's min-content is the wrapped width. */
  .message-text :deep(*) {
    overflow-wrap: anywhere;
    word-break: break-word;
    max-width: 100%;
  }

  .message-text :deep(p) {
    margin: 0;
  }

  .message-text :deep(a) {
    color: var(--cp-primary-600);
    font-weight: 500;
    text-decoration: underline;
  }

  .message-media {
    position: relative;
    width: 100%;
    max-width: 100%;
    border-radius: 6px;
    overflow: hidden;
  }

  /* Outer wrapper + VoiceNoteBubble root share one definite width so
     waveform and timeline chrome (time / 1x) stay aligned. */
  .message-media--voice,
  .message-media--voice-fixed {
    width: 260px;
    max-width: 100%;
  }

  .message-media-voice,
  .message-media-voice--fixed {
    width: 260px;
    max-width: 100%;
  }

  .message-media--voice {
    overflow: visible;
    border-radius: 0;
  }

  .message-media-voice {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
  }

  .message-meta {
    width: fit-content;
    max-width: 100%;
  }

  .message-media-image {
    width: 100%;
    max-width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 6px;
  }

  .message-media-video {
    width: 100%;
    height: 100%;
    overflow: hidden;
    border-radius: 8px;
  }

  .message-media-video-player {
    width: 100%;
    height: 100%;
  }

  .media-shimmer {
    position: absolute;
    inset: 0;
    border-radius: 8px;
    background: linear-gradient(90deg, var(--cp-font-100) 0%, var(--cp-border-500) 50%, var(--cp-font-100) 100%);
    background-size: 200% 100%;
    animation: shimmer 1.2s ease-in-out infinite;
    z-index: 1;
    pointer-events: none;
  }

  .chat-bubble-media :deep(.file-attachment-item) {
    border: none;
  }

  /* Timestamp - 10px, gray-700 */
  .timestamp {
    font-family: inherit;
    font-size: 10px;
    font-weight: 400;
    line-height: 1.5;
    color: var(--cp-font-700);
  }

  /* Failed message status */
  .message-status-failed {
    color: var(--cp-error-500);
  }

  /* Typing indicator */
  .bouncing-dots {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
  }

  .dot {
    width: 6px;
    height: 6px;
    background-color: var(--cp-font-300);
    border-radius: 50%;
    animation: bounce 1.4s infinite ease-in-out both;
  }

  .dot:nth-child(1) {
    animation-delay: -0.32s;
  }

  .dot:nth-child(2) {
    animation-delay: -0.16s;
  }

  @keyframes bounce {
    0%,
    80%,
    100% {
      transform: scale(0);
    }
    40% {
      transform: scale(1);
    }
  }

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
</style>
